import Plyr from 'plyr'
import type { VideoService } from '../types'

export class FilmPlayer {
  private filmContainer: HTMLElement | null
  private playerContainer: HTMLElement | null = null
  private videoWrapper: Element | null = null
  private player: Plyr | null = null
  private previousFocus: HTMLElement | null = null

  constructor() {
    this.filmContainer = document.querySelector('#js-single-film') as HTMLElement

    if (!this.filmContainer) {
      return
    }

    this.playerContainer = document.querySelector('#js-film-player') as HTMLElement
    this.videoWrapper = this.playerContainer?.querySelector('#js-video') || null

    this.bindButtons()
    this.bindKeys()
  }

  private readonly trapFocus = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab' || !this.playerContainer?.classList.contains('active')) return

    const focusable = this.playerContainer.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
    )
    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!first || !last) return

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  bindButtons(): void {
    const buttonOpen = document.querySelector('.js-open-player')
    const buttonClose = document.querySelector('.js-close-player')

    buttonOpen?.addEventListener('click', () => {
      this.previousFocus = document.activeElement as HTMLElement | null
      this.openPlayer()
    })

    buttonClose?.addEventListener('click', () => {
      this.closePlayer()
    })
  }

  bindKeys(): void {
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.playerContainer?.classList.contains('active')) {
        this.closePlayer()
      }
    })

    document.addEventListener('keydown', this.trapFocus)
  }

  openPlayer(): void {
    if (!this.filmContainer || !this.playerContainer || !this.videoWrapper) return

    const videoService = this.filmContainer.dataset.videoService as VideoService
    const videoServiceId = this.filmContainer.dataset.videoId

    if (!videoService || !videoServiceId) return

    if (this.player) {
      this.closePlayer()
    }

    document.body.classList.add('scroll-locked')
    this.playerContainer.classList.add('active')
    this.playerContainer.setAttribute('aria-hidden', 'false')

    let videoHtml = ''
    if (videoService === 'YouTube') {
      videoHtml = `<div id="js-video-iframe" data-plyr-provider="youtube" data-plyr-embed-id="${videoServiceId}"></div>`
    } else if (videoService === 'Vimeo') {
      videoHtml = `<div id="js-video-iframe" data-plyr-provider="vimeo" data-plyr-embed-id="${videoServiceId}"></div>`
    }

    if (!videoHtml) return

    this.videoWrapper.insertAdjacentHTML('beforeend', videoHtml)

    const videoElement = this.videoWrapper.querySelector('#js-video-iframe')
    if (videoElement) {
      this.player = new Plyr(videoElement as HTMLElement, {
        controls: ['play', 'progress', 'current-time', 'fullscreen'],
        autoplay: true,
        hideControls: true
      })

      this.player.once('ready', () => {
        const playResult = this.player?.play()
        if (playResult instanceof Promise) {
          playResult.catch(() => {})
        }
      })

      this.playerControls()
    }

    const closeButton = this.playerContainer.querySelector('.js-close-player') as HTMLElement | null
    closeButton?.focus()
  }

  closePlayer(): void {
    if (!this.playerContainer) return

    if (this.player) {
      this.player.destroy()
      this.player = null
    }

    this.playerContainer.classList.remove('active')
    this.playerContainer.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('scroll-locked')

    const videoIframe = this.videoWrapper?.querySelector('#js-video-iframe')
    videoIframe?.remove()

    this.previousFocus?.focus()
    this.previousFocus = null
  }

  playerControls(): void {
    if (!this.player || !this.playerContainer) return

    const metaBar = this.playerContainer.querySelector('.js-meta') as HTMLElement | null
    if (!metaBar) return

    const showDuration = 2000
    const fadeDuration = 200
    let mouseTimeout: ReturnType<typeof setTimeout> | null = null

    const showMetaBar = () => {
      metaBar.style.opacity = '1'
      metaBar.style.display = 'block'
    }

    const hideMetaBar = () => {
      metaBar.style.opacity = '0'
      setTimeout(() => {
        metaBar.style.display = 'none'
      }, fadeDuration)
    }

    document.addEventListener('mousemove', () => {
      if (mouseTimeout) {
        clearTimeout(mouseTimeout)
      }
      showMetaBar()

      if (!this.player?.paused) {
        mouseTimeout = setTimeout(() => {
          hideMetaBar()
        }, showDuration)
      }
    })

    this.player.on('play', () => {
      mouseTimeout = setTimeout(() => {
        hideMetaBar()
      }, showDuration)
    })

    this.player.on('pause', () => {
      if (mouseTimeout) {
        clearTimeout(mouseTimeout)
      }
      showMetaBar()
    })
  }
}
